import {
  getAudioContext,
  playbackLookaheadSec,
  scheduleSample,
  stopAllScheduledVoices,
} from './webAudioEngine';
import { celestialPadEngine, unlockCelestialPad } from './celestialPadEngine';
import type { PlaybackSchedule } from '../utils/playbackSchedule';
import {
  channelGain,
  type MixVolumes,
} from '../utils/mixVolumes';

export interface PlaybackSessionCallbacks {
  soundEnabled: boolean;
  onMelodyStep: (globalIndex: number, measureIndex: number) => void;
  onDrumStep: (drumStep: number) => void;
  onEnd: () => void;
}

export interface PlaybackSession {
  stop: () => void;
}

export function startPlaybackSession(
  schedule: PlaybackSchedule,
  callbacks: PlaybackSessionCallbacks,
  mixVolumes: MixVolumes,
): PlaybackSession {
  let cancelled = false;
  let rafId = 0;
  let endTimer: ReturnType<typeof setTimeout> | null = null;

  const stop = () => {
    if (cancelled) return;
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
    if (endTimer) clearTimeout(endTimer);
    stopAllScheduledVoices();
    celestialPadEngine.stopAll();
  };

  void (async () => {
    const ctx = await getAudioContext();

    if (callbacks.soundEnabled) {
      await unlockCelestialPad();
      celestialPadEngine.setHarmonyMixGain(channelGain('harmony', mixVolumes));
    }

    const boomGain = channelGain('boom', mixVolumes);
    const drumsGain = channelGain('drums', mixVolumes);

    const lookahead = playbackLookaheadSec();
    const startAt = ctx.currentTime + lookahead;
    const wallStartMs = performance.now() + lookahead * 1000;

    if (callbacks.soundEnabled) {
      for (const event of schedule.sounds) {
        if (cancelled) return;
        const when = startAt + event.timeSec;
        if (event.kind === 'note' && event.note) {
          scheduleSample(
            `boom:${event.note}`,
            when,
            boomGain,
            event.durationMs ? event.durationMs / 1000 : undefined,
          );
        } else if (event.kind === 'drum' && event.drum) {
          scheduleSample(`drum:${event.drum}`, when, drumsGain);
        } else if (event.kind === 'chord' && event.chord) {
          celestialPadEngine.scheduleStroke(
            event.chord,
            when,
            event.holdSec ?? 0.35,
            event.chordFrequencies,
          );
        }
      }
    }

    let uiIndex = 0;
    const uiEvents = schedule.ui;

    const tickUi = () => {
      if (cancelled) return;

      const elapsedSec = (performance.now() - wallStartMs) / 1000;

      while (uiIndex < uiEvents.length && uiEvents[uiIndex].timeSec <= elapsedSec) {
        const evt = uiEvents[uiIndex];
        uiIndex += 1;
        if (
          evt.kind === 'melody' &&
          evt.globalIndex !== undefined &&
          evt.measureIndex !== undefined
        ) {
          callbacks.onMelodyStep(evt.globalIndex, evt.measureIndex);
        } else if (evt.kind === 'drum' && evt.drumStep !== undefined) {
          callbacks.onDrumStep(evt.drumStep);
        }
      }

      if (elapsedSec >= schedule.totalDurationSec) {
        callbacks.onEnd();
        return;
      }

      rafId = requestAnimationFrame(tickUi);
    };

    rafId = requestAnimationFrame(tickUi);

    endTimer = setTimeout(() => {
      if (!cancelled) callbacks.onEnd();
    }, schedule.totalDurationSec * 1000 + lookahead * 1000 + 120);
  })();

  return { stop };
}
